import React, { useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  Page,
  PageHeader,
  StatusBadge,
  normalizeRunStatus,
  Text,
  Flex,
  Button,
  LoadingOrbit,
  useToast,
  DangerSection,
  DangerAction,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
} from '@chatsdet/ui';
import { useAuth } from '../../contexts/AuthContext';
import { RunTriageLockup } from '../../features/triage/RunTriageLockup';
import { Robot, Play, Stop, Trash, ArrowClockwise } from '@phosphor-icons/react';
import { OrgSwitchPrompt } from '../components/OrgSwitchPrompt';
import { ApiError } from '../../lib/api-error';
import { useRowLinkBuilder } from '../../lib/useRowLink';
import { normalizeFromRest } from '../../features/workflows/useRunSummaryData';
import { runSummaryLockupCounts } from '../../features/workflows/runTotals';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import {
  useAgenticRunDetail,
  useRerunAgenticWorkflow,
  useRerunTestExecution,
  useRetryFailedTestExecution,
  useCancelTestExecution,
  useDeleteTestExecution,
  formatDuration,
  formatRelativeTimeAgo,
} from '../../features/workflows/useAgenticRunDetail';
import { VisualChangesBadge } from './visual/VisualChangesBadge';
import { AgenticRunFailuresTable } from './AgenticRunDetail/AgenticRunFailuresTable';
import { getRetryableFailedCount } from '../../features/workflows/rerunAvailability';
import type { TestRunDetailSearchParams } from '../../router';

const executionLabelMap: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  completed_with_errors: 'Completed (Errors)',
  // Without this entry the badge falls through to the `skipped` colour key's
  // own label ("Skipped"), since that is what this status normalizes to for
  // colour. It is not a skip — the platform failed before a verdict existed.
  completed_with_inconclusive: 'Platform errors',
  canceled: 'Canceled',
};

export const AgenticRunDetail: React.FC = () => {
  const navigate = useNavigate();
  const { executionId } = useParams({ from: '/runs/test/$executionId' });
  const search = useSearch({ from: '/runs/test/$executionId' });
  const { toast } = useToast();
  const { user } = useAuth();

  const updateSearch = React.useCallback(
    (updates: Partial<TestRunDetailSearchParams>) =>
      navigate({
        to: '/runs/test/$executionId',
        params: { executionId },
        search: (prev: Record<string, unknown>) => {
          const prevParams = prev as TestRunDetailSearchParams;
          const merged: TestRunDetailSearchParams = { ...prevParams, ...updates };

          if (!merged.q) delete merged.q;
          if (!merged.statuses || merged.statuses.length === 0) delete merged.statuses;
          if (!merged.failuresOnly) delete merged.failuresOnly;
          if (!merged.retriedOnly) delete merged.retriedOnly;

          return merged;
        },
      }),
    [navigate, executionId]
  );

  const retryFailedMutation = useRetryFailedTestExecution();
  const { wrongOrgInfo, clearWrongOrgInfo, ...executionQuery } = useAgenticRunDetail(executionId);
  const rerunMutation = useRerunAgenticWorkflow();
  const rerunExecutionMutation = useRerunTestExecution();
  const cancelMutation = useCancelTestExecution();
  const deleteMutation = useDeleteTestExecution();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const execution = executionQuery.data?.execution;
  const testRuns = executionQuery.data?.testRuns ?? [];
  const isRunning = execution?.status === 'queued' || execution?.status === 'running';
  const retryableFailedCount = React.useMemo(
    () => getRetryableFailedCount(execution?.status, testRuns),
    [execution?.status, testRuns]
  );
  const canRetryFailed = !!execution && retryableFailedCount > 0;

  // Table rows know only a testRunId — route through the resolver (it forwards
  // to /runs/$runId). The result squares pass a richer item below and link
  // straight to the run when a workflowRunId is known.
  const handleOpenTestRunDetail = React.useCallback(
    (testRunId: string) =>
      navigate({
        to: '/runs/tests/$testRunId',
        params: { testRunId },
      }),
    [navigate]
  );
  // Same destination as an href string, so the failures table rows are links
  // (plain click navigates, cmd/ctrl-click opens the run in a new tab).
  const buildHref = useRowLinkBuilder();
  const testRunHref = React.useCallback(
    (testRunId: string) => buildHref({ to: '/runs/tests/$testRunId', params: { testRunId } }),
    [buildHref]
  );

  // Normalize data for the results + triage lockup
  const runSummaryData = React.useMemo(() => {
    if (!execution) return null;
    return normalizeFromRest(execution, testRuns);
  }, [execution, testRuns]);

  // Setup breadcrumbs
  useBreadcrumbs([
    { label: 'Runs', onPress: () => navigate({ to: '/runs' }) },
    { label: 'Test Runs', onPress: () => navigate({ to: '/runs/test' }) },
    { label: `Test Run ${executionId?.slice(0, 8) ?? ''}` },
  ]);

  // Handle rerun
  const handleRerun = async (testRunId: string) => {
    try {
      await rerunMutation.mutateAsync({ testRunId, executionId });
      toast.success('Re-running workflow test...');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to re-run workflow', {
        reportError: !(error instanceof ApiError && error.userError),
      });
    }
  };

  const handleRunSuiteAgain = async () => {
    try {
      const result = await rerunExecutionMutation.mutateAsync({
        sourceExecutionId: executionId,
      });
      if (!result.suiteId) {
        toast.error('No workflows were available to run again');
        return;
      }
      toast.success(
        `Started test run with ${result.enqueued} workflow${result.enqueued === 1 ? '' : 's'}`
      );
      navigate({ to: '/runs/test/$executionId', params: { executionId: result.suiteId } });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start test run');
    }
  };

  const handleRetryAllFailed = async () => {
    if (!executionId) return;
    try {
      const result = await retryFailedMutation.mutateAsync(executionId);
      toast.success(
        `Retrying ${result.data.retriedCount} failed workflow${result.data.retriedCount === 1 ? '' : 's'}...`
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to retry failed workflows', {
        reportError: !(error instanceof ApiError && error.userError),
      });
    }
  };

  const handleViewWorkflow = React.useCallback(
    (workflowId: string) =>
      navigate({
        to: '/flows/$workflowId',
        params: { workflowId },
      }),
    [navigate]
  );

  // Loading state
  if (executionQuery.isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }

  // Wrong org state
  if (wrongOrgInfo) {
    return (
      <Page>
        <OrgSwitchPrompt
          resourceType="test run"
          targetOrgId={wrongOrgInfo.orgId}
          targetOrgName={wrongOrgInfo.orgName}
          redirectUrl={`/runs/test/${executionId}`}
          onCancel={() => {
            clearWrongOrgInfo();
            navigate({ to: '/runs' });
          }}
        />
      </Page>
    );
  }

  // Error state
  if (executionQuery.isError || !execution) {
    return (
      <Page>
        <PageHeader
          title="Execution Not Found"
          description="The requested test run could not be found"
        />
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Text className="text-muted-foreground">
            This execution may have been deleted or you may not have access.
          </Text>
          <Button
            variant="outline"
            size="sm"
            onPress={() => navigate({ to: '/runs' })}
            className="mt-4"
          >
            Back to Runs
          </Button>
        </div>
      </Page>
    );
  }

  // Header description
  const headerDescription = (
    <Flex align="center" gap="xs" wrap>
      <Text size="sm" className="text-muted-foreground">
        Duration {formatDuration(execution.startedAt, execution.finishedAt)}
      </Text>
      <Text size="sm" className="text-muted-foreground">
        /
      </Text>
      <Text size="sm" className="text-muted-foreground">
        Started {formatRelativeTimeAgo(execution.startedAt ?? execution.createdAt)}
      </Text>
      <Text size="sm" className="text-muted-foreground">
        /
      </Text>
      <Text size="sm" className="text-muted-foreground">
        {execution.completedWorkflows}/{execution.totalWorkflows} workflows
      </Text>
      <Text size="sm" className="text-muted-foreground">
        /
      </Text>
      <Text size="sm" className="text-muted-foreground">
        Started by {execution.triggeredBy?.displayName ?? execution.triggeredBy?.email ?? 'system'}
      </Text>
      <VisualChangesBadge scope={{ executionId }} />
      {execution.suiteName && execution.suiteId && (
        <>
          <Text size="sm" className="text-muted-foreground">
            /
          </Text>
          <Text size="sm" className="text-muted-foreground">
            Suite{' '}
            <Link
              to="/suites/$suiteId"
              params={{ suiteId: execution.suiteId }}
              className="font-medium text-foreground hover:underline"
            >
              {execution.suiteName}
            </Link>
          </Text>
        </>
      )}
    </Flex>
  );

  const resultsContent = (
    <>
        {/* Run results + AI triage */}
        {runSummaryData && (
          <RunTriageLockup
            counts={runSummaryLockupCounts(runSummaryData)}
            items={runSummaryData.items}
            triageSubject={{ kind: 'execution', id: runSummaryData.executionId }}
            isRunInProgress={
              runSummaryData.status === 'running' || runSummaryData.status === 'queued'
            }
            finishedAt={runSummaryData.finishedAt}
            onItemSelect={(item) =>
              item.workflowRunId
                ? navigate({
                    to: '/runs/$runId',
                    params: { runId: item.workflowRunId },
                    search: { testRunId: item.id },
                  })
                : handleOpenTestRunDetail(item.id)
            }
          />
        )}

        <AgenticRunFailuresTable
          testRuns={testRuns}
          isRunning={isRunning}
          detailRowsUnavailable={runSummaryData?.detailRowsUnavailable ?? false}
          completedWorkflows={execution.completedWorkflows}
          totalWorkflows={execution.totalWorkflows}
          getRowHref={(run) => testRunHref(run.id)}
          onOpenTestRunDetail={handleOpenTestRunDetail}
          onRerun={handleRerun}
          onViewWorkflow={handleViewWorkflow}
          isRerunPending={rerunMutation.isPending}
          filters={search}
          onFilterChange={updateSearch}
        />
    </>
  );

  return (
    <Page>
      <PageHeader
        icon={<Robot size={24} weight="fill" />}
        eyebrow={
          <StatusBadge
            status={normalizeRunStatus(execution.status)}
            label={executionLabelMap[execution.status]}
          />
        }
        title="Test Run"
        description={headerDescription}
        actions={
          <Flex align="center" gap="sm">
            {isRunning && (
              <Button
                variant="destructive"
                size="sm"
                onPress={async () => {
                  if (!executionId) return;
                  try {
                    await cancelMutation.mutateAsync(executionId);
                    toast.success('Test run canceled');
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : 'Failed to cancel');
                  }
                }}
                isDisabled={cancelMutation.isPending}
                iconLeft={<Stop size={16} />}
              >
                {cancelMutation.isPending ? 'Canceling...' : 'Cancel Run'}
              </Button>
            )}
            {canRetryFailed && (
              <Button
                variant="outline"
                size="sm"
                onPress={handleRetryAllFailed}
                isDisabled={retryFailedMutation.isPending}
                iconLeft={<ArrowClockwise size={16} />}
              >
                {retryFailedMutation.isPending
                  ? 'Retrying...'
                  : `Retry all failed (${retryableFailedCount})`}
              </Button>
            )}
            {!isRunning && (
              <Button
                variant="primary"
                size="sm"
                onPress={handleRunSuiteAgain}
                isDisabled={rerunExecutionMutation.isPending || testRuns.length === 0}
                iconLeft={<Play size={16} weight="fill" />}
              >
                {rerunExecutionMutation.isPending ? 'Starting...' : 'New Run'}
              </Button>
            )}
          </Flex>
        }
      />

      <div className="space-y-6">
        {resultsContent}

        {/* Danger Zone */}
        {!isRunning && (
          <DangerSection>
            <DangerAction
              title="Delete Execution"
              description="Permanently delete this test execution and all its test run results."
            >
              <Button
                variant="destructive"
                size="sm"
                onPress={() => setDeleteDialogOpen(true)}
                isDisabled={deleteMutation.isPending}
                iconLeft={<Trash size={16} />}
              >
                Delete Execution
              </Button>
            </DangerAction>
          </DangerSection>
        )}
      </div>

      <DialogTrigger isOpen={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} size="sm">
        <span />
        <Dialog
          title="Delete test execution"
          description="Are you sure you want to delete this execution?"
        >
          <DialogContent>
            <Text>
              This will permanently delete the execution and all its test run results. This action
              cannot be undone.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setDeleteDialogOpen(false)}
              isDisabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={async () => {
                if (!executionId) return;
                try {
                  await deleteMutation.mutateAsync(executionId);
                  toast.success('Execution deleted');
                  navigate({ to: '/runs/test' });
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to delete');
                }
                setDeleteDialogOpen(false);
              }}
              isDisabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Execution'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Page>
  );
};
